import { X, Sparkles, Gamepad2, Music, Palette, Box, MousePointer, Video, Waves, Smartphone } from 'lucide-react'
import { useStore } from '../store/useStore'
import { showToast } from './ui/Toast'

const TEMPLATES = [
  {
    id: 'particles',
    name: 'Particle System',
    description: 'Interactive particles that follow your mouse',
    icon: Sparkles,
    color: 'text-purple-400',
    code: `// Particle System Template
let particles = [];

function setup() {
  createCanvas(windowWidth, windowHeight);
  for (let i = 0; i < 100; i++) {
    particles.push(new Particle(random(width), random(height)));
  }
}

function draw() {
  background(20, 20, 30, 25);
  
  for (let p of particles) {
    p.update();
    p.display();
    p.attract(mouseX, mouseY);
  }
}

class Particle {
  constructor(x, y) {
    this.pos = createVector(x, y);
    this.vel = createVector(random(-1, 1), random(-1, 1));
    this.size = random(3, 8);
    this.color = color(random(150, 255), random(100, 200), random(200, 255), 150);
  }
  
  attract(tx, ty) {
    let target = createVector(tx, ty);
    let force = p5.Vector.sub(target, this.pos);
    force.setMag(0.05);
    this.vel.add(force);
    this.vel.limit(4);
  }
  
  update() {
    this.pos.add(this.vel);
    if (this.pos.x < 0 || this.pos.x > width) this.vel.x *= -1;
    if (this.pos.y < 0 || this.pos.y > height) this.vel.y *= -1;
  }
  
  display() {
    noStroke();
    fill(this.color);
    ellipse(this.pos.x, this.pos.y, this.size);
  }
}

function windowResized() {
  resizeCanvas(windowWidth, windowHeight);
}`
  },
  {
    id: 'game',
    name: 'Simple Game',
    description: 'Catch the falling objects game starter',
    icon: Gamepad2,
    color: 'text-green-400',
    code: `// Simple Catch Game Template
let player;
let items = [];
let score = 0;

function setup() {
  createCanvas(windowWidth, windowHeight);
  player = { x: width / 2, y: height - 50, w: 80, h: 20 };
}

function draw() {
  background(30, 30, 40);
  
  // Spawn items
  if (frameCount % 60 === 0) {
    items.push({
      x: random(20, width - 20),
      y: 0,
      size: random(15, 30),
      speed: random(2, 5),
      color: color(random(255), random(255), random(255))
    });
  }
  
  // Update & draw items
  for (let i = items.length - 1; i >= 0; i--) {
    let item = items[i];
    item.y += item.speed;
    
    fill(item.color);
    noStroke();
    ellipse(item.x, item.y, item.size);
    
    // Check collision with player
    if (item.y + item.size/2 > player.y && 
        item.x > player.x && 
        item.x < player.x + player.w) {
      score += 10;
      items.splice(i, 1);
    }
    
    // Remove if off screen
    if (item.y > height) {
      items.splice(i, 1);
    }
  }
  
  // Draw player
  player.x = mouseX - player.w / 2;
  player.x = constrain(player.x, 0, width - player.w);
  fill(100, 200, 255);
  rect(player.x, player.y, player.w, player.h, 5);
  
  // Score
  fill(255);
  textSize(24);
  textAlign(LEFT, TOP);
  text('Score: ' + score, 20, 20);
}

function windowResized() {
  resizeCanvas(windowWidth, windowHeight);
}`
  },
  {
    id: 'audio',
    name: 'Audio Reactive',
    description: 'Visualize microphone input with shapes',
    icon: Music,
    color: 'text-pink-400',
    code: `// Audio Reactive Template
let mic;
let fft;

function setup() {
  createCanvas(windowWidth, windowHeight);
  
  // Click to start audio
  userStartAudio();
  
  mic = new p5.AudioIn();
  mic.start();
  
  fft = new p5.FFT();
  fft.setInput(mic);
  
  colorMode(HSB, 360, 100, 100, 100);
}

function draw() {
  background(0, 0, 10, 20);
  
  let spectrum = fft.analyze();
  let vol = mic.getLevel();
  
  // Draw spectrum bars
  noStroke();
  for (let i = 0; i < spectrum.length; i += 4) {
    let x = map(i, 0, spectrum.length, 0, width);
    let h = map(spectrum[i], 0, 255, 0, height * 0.8);
    let hue = map(i, 0, spectrum.length, 0, 360);
    
    fill(hue, 80, 90, 80);
    rect(x, height - h, width / spectrum.length * 4 - 2, h);
  }
  
  // Center circle based on volume
  let size = map(vol, 0, 0.5, 50, 400);
  fill(frameCount % 360, 70, 90, 50);
  ellipse(width / 2, height / 2, size);
  
  // Instructions
  fill(0, 0, 100);
  textSize(14);
  textAlign(CENTER, TOP);
  text('Click to enable audio • Make some noise!', width / 2, 20);
}

function windowResized() {
  resizeCanvas(windowWidth, windowHeight);
}`
  },
  {
    id: 'generative',
    name: 'Generative Art',
    description: 'Create unique algorithmic patterns',
    icon: Palette,
    color: 'text-orange-400',
    code: `// Generative Art Template
let seed;

function setup() {
  createCanvas(windowWidth, windowHeight);
  colorMode(HSB, 360, 100, 100, 100);
  seed = random(10000);
  generate();
}

function generate() {
  randomSeed(seed);
  noiseSeed(seed);
  background(220, 10, 95);
  
  let cols = 20;
  let rows = 20;
  let cellW = width / cols;
  let cellH = height / rows;
  
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      let px = x * cellW + cellW / 2;
      let py = y * cellH + cellH / 2;
      let n = noise(x * 0.1, y * 0.1);
      
      push();
      translate(px, py);
      rotate(n * TWO_PI);
      
      let hue = map(n, 0, 1, 180, 300);
      fill(hue, 70, 90, 80);
      noStroke();
      
      let shape = floor(random(3));
      let size = cellW * 0.8 * n;
      
      if (shape === 0) {
        ellipse(0, 0, size, size);
      } else if (shape === 1) {
        rectMode(CENTER);
        rect(0, 0, size, size);
      } else {
        triangle(-size/2, size/2, size/2, size/2, 0, -size/2);
      }
      pop();
    }
  }
}

function mousePressed() {
  seed = random(10000);
  generate();
}

function keyPressed() {
  if (key === 's') {
    saveCanvas('generative-art', 'png');
  }
}

function windowResized() {
  resizeCanvas(windowWidth, windowHeight);
  generate();
}`
  },
  {
    id: '3d',
    name: '3D Scene',
    description: 'Interactive WEBGL 3D environment',
    icon: Box,
    color: 'text-cyan-400',
    code: `// 3D Scene Template
let angle = 0;

function setup() {
  createCanvas(windowWidth, windowHeight, WEBGL);
  colorMode(HSB, 360, 100, 100);
}

function draw() {
  background(240, 20, 15);
  
  // Lighting
  ambientLight(60);
  directionalLight(255, 255, 255, 0.5, 0.5, -1);
  pointLight(200, 100, 100, mouseX - width/2, mouseY - height/2, 200);
  
  // Camera orbit
  orbitControl();
  
  // Rotating cubes
  for (let i = 0; i < 5; i++) {
    push();
    let hue = (frameCount + i * 50) % 360;
    
    rotateY(angle + i * 0.5);
    rotateX(angle * 0.5);
    translate(100 + i * 30, 0, 0);
    
    fill(hue, 70, 90);
    stroke(hue, 70, 50);
    strokeWeight(2);
    box(40 - i * 5);
    pop();
  }
  
  // Center sphere
  push();
  fill(frameCount % 360, 60, 80);
  noStroke();
  sphere(50);
  pop();
  
  // Ground plane
  push();
  rotateX(HALF_PI);
  translate(0, 0, 100);
  fill(200, 30, 30);
  noStroke();
  plane(500, 500);
  pop();
  
  angle += 0.01;
}

function windowResized() {
  resizeCanvas(windowWidth, windowHeight);
}`
  },
  {
    id: 'interactive',
    name: 'Drawing Canvas',
    description: 'Paint and draw with your mouse/finger',
    icon: MousePointer,
    color: 'text-yellow-400',
    code: `// Drawing Canvas Template
let brushSize = 20;
let brushColor;
let hue = 0;

function setup() {
  createCanvas(windowWidth, windowHeight);
  colorMode(HSB, 360, 100, 100, 100);
  background(0, 0, 95);
  brushColor = color(0, 80, 90);
}

function draw() {
  // Slowly cycle hue
  hue = (hue + 0.5) % 360;
  brushColor = color(hue, 80, 90, 50);
}

function mouseDragged() {
  noStroke();
  fill(brushColor);
  
  // Smooth line between points
  let d = dist(pmouseX, pmouseY, mouseX, mouseY);
  let steps = max(1, floor(d / 2));
  
  for (let i = 0; i < steps; i++) {
    let x = lerp(pmouseX, mouseX, i / steps);
    let y = lerp(pmouseY, mouseY, i / steps);
    ellipse(x, y, brushSize);
  }
}

function touchMoved() {
  mouseDragged();
  return false; // Prevent scrolling
}

function keyPressed() {
  if (key === 'c') {
    background(0, 0, 95);
  }
  if (key === '+' || key === '=') {
    brushSize = min(100, brushSize + 5);
  }
  if (key === '-') {
    brushSize = max(5, brushSize - 5);
  }
  if (key === 's') {
    saveCanvas('drawing', 'png');
  }
}

function mousePressed() {
  // Double tap to clear on mobile
  if (mouseButton === LEFT && mouseX < 60 && mouseY < 60) {
    background(0, 0, 95);
  }
}

function windowResized() {
  // Don't resize - preserve drawing
}`
  },
  {
    id: 'webcam',
    name: 'Webcam Effects',
    description: 'Apply real-time filters to camera',
    icon: Video,
    color: 'text-red-400',
    code: `// Webcam Effects Template
let video;
let effectMode = 0;
const effects = ['Normal', 'Pixelate', 'ASCII', 'Edge', 'Posterize'];

function setup() {
  createCanvas(windowWidth, windowHeight);
  video = createCapture(VIDEO);
  video.size(640, 480);
  video.hide();
  textFont('monospace');
}

function draw() {
  background(0);
  
  video.loadPixels();
  
  if (effectMode === 0) {
    // Normal
    image(video, 0, 0, width, height);
  } 
  else if (effectMode === 1) {
    // Pixelate
    let res = 10;
    for (let y = 0; y < video.height; y += res) {
      for (let x = 0; x < video.width; x += res) {
        let i = (y * video.width + x) * 4;
        let r = video.pixels[i];
        let g = video.pixels[i + 1];
        let b = video.pixels[i + 2];
        fill(r, g, b);
        noStroke();
        let px = map(x, 0, video.width, 0, width);
        let py = map(y, 0, video.height, 0, height);
        let pw = width / video.width * res;
        let ph = height / video.height * res;
        rect(px, py, pw, ph);
      }
    }
  }
  else if (effectMode === 2) {
    // ASCII
    let chars = ' .:-=+*#%@';
    textSize(10);
    fill(0, 255, 0);
    for (let y = 0; y < video.height; y += 8) {
      for (let x = 0; x < video.width; x += 5) {
        let i = (y * video.width + x) * 4;
        let br = (video.pixels[i] + video.pixels[i+1] + video.pixels[i+2]) / 3;
        let charIndex = floor(map(br, 0, 255, 0, chars.length));
        let px = map(x, 0, video.width, 0, width);
        let py = map(y, 0, video.height, 0, height);
        text(chars[charIndex], px, py);
      }
    }
  }
  else if (effectMode === 3) {
    // Edge detection (simple)
    loadPixels();
    for (let y = 1; y < video.height - 1; y++) {
      for (let x = 1; x < video.width - 1; x++) {
        let i = (y * video.width + x) * 4;
        let i2 = ((y + 1) * video.width + x + 1) * 4;
        let diff = abs(video.pixels[i] - video.pixels[i2]);
        let c = diff > 20 ? 255 : 0;
        let px = floor(map(x, 0, video.width, 0, width));
        let py = floor(map(y, 0, video.height, 0, height));
        stroke(c);
        point(px, py);
      }
    }
  }
  else if (effectMode === 4) {
    // Posterize
    image(video, 0, 0, width, height);
    filter(POSTERIZE, 4);
  }
  
  // UI
  fill(255);
  textSize(16);
  textAlign(LEFT, TOP);
  text('Effect: ' + effects[effectMode] + ' (click to change)', 20, 20);
}

function mousePressed() {
  effectMode = (effectMode + 1) % effects.length;
}

function windowResized() {
  resizeCanvas(windowWidth, windowHeight);
}`
  },
  {
    id: 'waves',
    name: 'Wave Simulation',
    description: 'Mesmerizing animated wave patterns',
    icon: Waves,
    color: 'text-blue-400',
    code: `// Wave Simulation Template
let t = 0;

function setup() {
  createCanvas(windowWidth, windowHeight);
  colorMode(HSB, 360, 100, 100, 100);
  strokeWeight(2);
}

function draw() {
  background(220, 30, 15);
  
  let rows = 30;
  let amplitude = 50;
  
  for (let row = 0; row < rows; row++) {
    let y = map(row, 0, rows, 100, height - 100);
    let hue = map(row, 0, rows, 180, 280);
    
    stroke(hue, 70, 90, 80);
    noFill();
    
    beginShape();
    for (let x = 0; x < width; x += 5) {
      let wave1 = sin(x * 0.01 + t + row * 0.2) * amplitude;
      let wave2 = sin(x * 0.02 - t * 0.5 + row * 0.1) * amplitude * 0.5;
      let wave3 = cos(x * 0.005 + t * 0.3) * amplitude * 0.3;
      
      let mouseInfluence = 0;
      let d = dist(x, y + wave1, mouseX, mouseY);
      if (d < 150) {
        mouseInfluence = map(d, 0, 150, 50, 0);
      }
      
      let py = y + wave1 + wave2 + wave3 + mouseInfluence;
      vertex(x, py);
    }
    endShape();
  }
  
  t += 0.02;
  
  // Instructions
  fill(0, 0, 100, 80);
  textSize(14);
  textAlign(CENTER, BOTTOM);
  text('Move mouse to interact with waves', width / 2, height - 20);
}

function windowResized() {
  resizeCanvas(windowWidth, windowHeight);
}`
  },
  {
    id: 'sensors',
    name: 'Device Sensors',
    description: 'Control with accelerometer & gyroscope (mobile)',
    icon: Smartphone,
    color: 'text-emerald-400',
    code: `// Device Sensors Template
// Tilt your phone to control the ball!

let ballX, ballY;
let velX = 0, velY = 0;
let accelX = 0, accelY = 0, accelZ = 0;
let rotationX = 0, rotationY = 0, rotationZ = 0;
let sensorPermissionGranted = false;

function setup() {
  createCanvas(windowWidth, windowHeight);
  ballX = width / 2;
  ballY = height / 2;
  textAlign(CENTER, CENTER);
  
  // iOS 13+ requires user interaction for sensors
  if (typeof DeviceOrientationEvent !== 'undefined' && 
      typeof DeviceOrientationEvent.requestPermission === 'function') {
    // Show tap message
  } else {
    // Non-iOS - sensors work automatically
    sensorPermissionGranted = true;
    enableSensors();
  }
}

function draw() {
  background(30, 35, 45);
  
  if (!sensorPermissionGranted) {
    // Prompt to enable sensors
    fill(255);
    textSize(20);
    text('👆 Tap to enable sensors', width/2, height/2);
    textSize(14);
    fill(150);
    text('Then tilt your device to control the ball', width/2, height/2 + 40);
    return;
  }
  
  // Physics: Apply tilt as acceleration
  velX += rotationY * 0.1;  // Left-right tilt
  velY += rotationX * 0.1;  // Front-back tilt
  
  // Friction
  velX *= 0.98;
  velY *= 0.98;
  
  // Update position
  ballX += velX;
  ballY += velY;
  
  // Bounce off walls
  if (ballX < 30) { ballX = 30; velX *= -0.7; }
  if (ballX > width - 30) { ballX = width - 30; velX *= -0.7; }
  if (ballY < 30) { ballY = 30; velY *= -0.7; }
  if (ballY > height - 30) { ballY = height - 30; velY *= -0.7; }
  
  // Draw ball with gradient
  noStroke();
  for (let i = 30; i > 0; i--) {
    let c = map(i, 0, 30, 100, 255);
    fill(100, c, 255, map(i, 0, 30, 255, 50));
    ellipse(ballX, ballY, i * 2);
  }
  
  // Show sensor values
  fill(200);
  textSize(12);
  textAlign(LEFT, TOP);
  text('Tilt X: ' + rotationX.toFixed(1) + '°', 20, 20);
  text('Tilt Y: ' + rotationY.toFixed(1) + '°', 20, 40);
  text('Accel: ' + accelX.toFixed(1) + ', ' + accelY.toFixed(1), 20, 60);
}

function touchStarted() {
  if (!sensorPermissionGranted) {
    requestSensorPermission();
  }
  return false;
}

async function requestSensorPermission() {
  try {
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      const permission = await DeviceOrientationEvent.requestPermission();
      if (permission === 'granted') {
        sensorPermissionGranted = true;
        enableSensors();
      }
    }
  } catch (e) {
    console.log('Sensor permission error:', e);
  }
}

function enableSensors() {
  window.addEventListener('deviceorientation', (e) => {
    rotationX = e.beta || 0;   // -180 to 180 (front-back)
    rotationY = e.gamma || 0;  // -90 to 90 (left-right)
    rotationZ = e.alpha || 0;  // 0 to 360 (compass)
  });
  
  window.addEventListener('devicemotion', (e) => {
    const accel = e.accelerationIncludingGravity;
    if (accel) {
      accelX = accel.x || 0;
      accelY = accel.y || 0;
      accelZ = accel.z || 0;
    }
  });
}

function windowResized() {
  resizeCanvas(windowWidth, windowHeight);
  ballX = constrain(ballX, 30, width - 30);
  ballY = constrain(ballY, 30, height - 30);
}`
  }
];

export default function TemplateModal({ onClose }) {
  const { setFiles, setSketchName, setSaved } = useStore()

  const handleSelectTemplate = (template) => {
    // Create new files with template code
    const newFiles = [
      {
        id: 'sketch-' + Date.now(),
        name: 'sketch.js',
        content: template.code,
        type: 'javascript'
      },
      {
        id: 'index-' + Date.now(),
        name: 'index.html',
        content: `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${template.name}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { overflow: hidden; height: 100%; }
    canvas { display: block; }
  </style>
</head>
<body>
  <script src="sketch.js"></script>
</body>
</html>`,
        type: 'html'
      }
    ]

    setFiles(newFiles)
    setSketchName(template.name)
    setSaved(false)
    showToast(`Started with "${template.name}" template`, 'success')
    
    // Trigger haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(50)
    }
    
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-ide-surface border border-ide-border rounded-xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-modal animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-ide-border">
          <div>
            <h2 className="text-lg font-semibold text-ide-text">Start from Template</h2>
            <p className="text-sm text-ide-textMuted">Choose a template to get started quickly</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover rounded-lg transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Templates Grid */}
        <div className="p-4 overflow-y-auto max-h-[calc(85vh-80px)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TEMPLATES.map((template) => {
              const Icon = template.icon
              return (
                <button
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  className="flex items-start gap-3 p-4 bg-ide-bg border border-ide-border rounded-lg hover:border-ide-borderHover hover:bg-ide-elevated transition-all text-left group active:scale-[0.98]"
                >
                  <div className={`p-2 rounded-lg bg-ide-surface ${template.color}`}>
                    <Icon size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-ide-text group-hover:text-white transition-colors">
                      {template.name}
                    </h3>
                    <p className="text-sm text-ide-textMuted mt-0.5 line-clamp-2">
                      {template.description}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
